import { createError, roundToNearest, USER_ROUNDING_NUM_DECIMALS, US_COUNTRY_CODE } from '@common/neo.tax';
import { useEffect, useRef, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useUpdateRdExpenseProjectAllocations, RdProject, type RdExpenseProjectAllocation } from '@neo.tax/rd/hooks';
import { RdExpense } from '@neo.tax/rd/hooks/useRdExpense';
import { RdAllocationsInPeriod, RdProjectAllocation } from '@neo.tax/rd/types';
import { getRdCategoryBadgeConfig } from '@neo.tax/rd/utils';
import { AllocationModal, updateNonProjectTimePercent, updateProjectAllocationPercent } from '@neo.tax/shared';

import { getInitialModalAllocationPercents, getVisibleProjectAllocations } from './qre-allocation.utils';

type EditQreExpenseAllocationModalProps = {
  rdExpense: RdExpense;
  close: () => void;
  isOpen: boolean;
  filteredRdExpenseProjectAllocations: RdExpenseProjectAllocation[] | null;
  filteredRdExpenseProjectAllocationsLoading: boolean;
  allRdExpenseProjectAllocations: RdExpenseProjectAllocation[] | null;
  allRdExpenseProjectAllocationsLoading: boolean;
  selectedRdProject?: RdProject;
  searchKeyword: string | null;
  setSearchKeyword: React.Dispatch<React.SetStateAction<string | null>>;
};

const allocationsHaveChanges = (allocationsInPeriod: RdAllocationsInPeriod): boolean => {
  const {
    projectPercents: { projectIdToProjectAllocations },
  } = allocationsInPeriod;
  let hasUnsavedChanges = false;

  for (const projectAllocation of projectIdToProjectAllocations.values()) {
    if (projectAllocation.allocationPercent !== projectAllocation.originalAllocationPercent) {
      hasUnsavedChanges = true;
      break;
    }
  }

  hasUnsavedChanges =
    hasUnsavedChanges ||
    allocationsInPeriod.nonProjectPercent.allocationPercent !==
      allocationsInPeriod.nonProjectPercent.originalAllocationPercent;

  return hasUnsavedChanges;
};

const getTotalAllocationPercent = (allocationsInPeriod: RdAllocationsInPeriod): number => {
  const {
    nonProjectPercent,
    projectPercents: { projectIdToProjectAllocations },
  } = allocationsInPeriod;

  let totalAllocationPercent = nonProjectPercent.allocationPercent;
  for (const projectAllocation of projectIdToProjectAllocations.values()) {
    totalAllocationPercent += projectAllocation.allocationPercent;
  }

  return totalAllocationPercent;
};

export const EditQreExpenseAllocationModal = ({
  allRdExpenseProjectAllocations,
  allRdExpenseProjectAllocationsLoading,
  close,
  filteredRdExpenseProjectAllocations,
  filteredRdExpenseProjectAllocationsLoading,
  isOpen,
  rdExpense,
  searchKeyword,
  selectedRdProject,
  setSearchKeyword,
}: EditQreExpenseAllocationModalProps): React.ReactElement => {
  const { fiscalYear, period } = useRdContext();

  const [modalAllocationPercents, setModalAllocationPercents] = useState<RdAllocationsInPeriod>(() => {
    if (!allRdExpenseProjectAllocations) {
      return {
        nonProjectPercent: {
          allocationPercent: 100,
          originalAllocationPercent: 100,
          lastAllocationPercentOnProjectPercentUpdate: 100,
        },
        projectPercents: {
          projectIdToProjectAllocations: new Map(),
          selectedIdToProjectAllocations: new Map(),
        },
      };
    }
    return getInitialModalAllocationPercents(allRdExpenseProjectAllocations, selectedRdProject);
  });

  const isMountedRef = useRef(true);
  const hasInitializedRef = useRef(false);

  // Update modal allocation percents when all data loads (only once)
  useEffect(() => {
    if (
      allRdExpenseProjectAllocations &&
      !allRdExpenseProjectAllocationsLoading &&
      isMountedRef.current &&
      !hasInitializedRef.current
    ) {
      setModalAllocationPercents(getInitialModalAllocationPercents(allRdExpenseProjectAllocations, selectedRdProject));
      hasInitializedRef.current = true;
    }
  }, [allRdExpenseProjectAllocations, allRdExpenseProjectAllocationsLoading, selectedRdProject]);

  // Reset initialization flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasInitializedRef.current = false;
    }
  }, [isOpen]);

  // Update modal state with filtered results when searching
  useEffect(() => {
    if (
      isOpen &&
      searchKeyword &&
      filteredRdExpenseProjectAllocations &&
      !filteredRdExpenseProjectAllocationsLoading &&
      isMountedRef.current
    ) {
      setModalAllocationPercents((currentState) => {
        const newState = { ...currentState };
        const projectMap = newState.projectPercents.projectIdToProjectAllocations;

        for (const allocation of filteredRdExpenseProjectAllocations) {
          if (!projectMap.has(allocation.rdProjectId)) {
            const allocationPercent = roundToNearest(
              allocation.allocationPercentNotRounded,
              USER_ROUNDING_NUM_DECIMALS,
            );
            const rdProjectAllocation: RdProjectAllocation = {
              projectId: allocation.rdProjectId,
              projectName: allocation.project.name.trim(),
              allocationPercent,
              originalAllocationPercent: allocationPercent,
              lastAllocationPercentOnProjectPercentUpdate: allocationPercent,
              consideredForRd: allocation.project.consideredForRd,
              isSection174Eligible: allocation.project.isSection174Eligible,
            };
            projectMap.set(allocation.rdProjectId, rdProjectAllocation);
          }
        }

        return newState;
      });
    }
  }, [isOpen, searchKeyword, filteredRdExpenseProjectAllocations, filteredRdExpenseProjectAllocationsLoading]);

  // Cleanup function to prevent state updates after unmount
  useEffect(
    () => (): void => {
      isMountedRef.current = false;
    },
    [],
  );

  const { loading: loadingUpdateAllocations, updateRdExpenseProjectAllocations } = useUpdateRdExpenseProjectAllocations(
    {
      onSuccess: close,
    },
  );

  const updateNonProjectPercent = (nonProjectPercent: number): void => {
    setModalAllocationPercents((currentAllocationPercents) => {
      if (!currentAllocationPercents) {
        throw createError('MissingAllocationPercentsError', 'Allocation percents should be set before updating');
      }
      return updateNonProjectTimePercent({
        allocationsInPeriod: currentAllocationPercents,
        newAllocationPercent: nonProjectPercent,
      });
    });
  };

  const updateProjectAllocation = ({
    allocationPercent,
    id: projectId,
  }: {
    id: string;
    allocationPercent: number;
  }): void => {
    setModalAllocationPercents((currentAllocationPercents) => {
      if (!currentAllocationPercents) {
        throw createError('MissingAllocationPercentsError', 'Allocation percents should be set before updating');
      }
      return updateProjectAllocationPercent({
        allocationsInPeriod: currentAllocationPercents,
        newAllocationPercent: allocationPercent,
        projectId: projectId,
      });
    });
  };

  // Filter project allocations to only show those that match the search keyword
  const filteredProjectIds = new Set(
    filteredRdExpenseProjectAllocations?.map((allocation) => allocation.rdProjectId) ?? [],
  );

  const projectAllocations = getVisibleProjectAllocations(
    modalAllocationPercents.projectPercents.projectIdToProjectAllocations,
    filteredProjectIds,
    selectedRdProject?.id,
  );

  const submit = async (): Promise<void> => {
    // Use all project allocations from the modal state, not just the filtered ones
    const allProjectAllocations = [...modalAllocationPercents.projectPercents.projectIdToProjectAllocations.values()];
    const rdProjectAllocationPercents = allProjectAllocations.map(({ allocationPercent, projectId }) => ({
      allocationPercent,
      rdProjectId: projectId,
    }));
    await updateRdExpenseProjectAllocations(rdExpense.id, rdProjectAllocationPercents);
  };

  const totalAllocationPercent = getTotalAllocationPercent(modalAllocationPercents);

  const hasUnsavedChanges = allocationsHaveChanges(modalAllocationPercents);
  const qre = rdExpense.payrollQre || rdExpense.accountingQre;

  if (!qre) {
    throw createError('MissingQreError', 'rdExpense has neither of `payrollQre` or `accountingQre`');
  }

  const isForeignExpense = qre.country !== US_COUNTRY_CODE;

  return (
    <AllocationModal
      period={period}
      isOpen={isOpen}
      close={close}
      expenseName={qre?.name.trim() ?? ''}
      nonProjectPercent={modalAllocationPercents.nonProjectPercent.allocationPercent}
      nonProjectAllocationTitle="Non-R&D Allocation"
      nonProjectAllocationHelpText={
        searchKeyword
          ? 'Editing the Non-R&D Allocation will adjust all projects with allocation proportionally.'
          : undefined
      }
      endOfListWarningText="Use the search box above if you don't see your project."
      updateNonProjectPercent={updateNonProjectPercent}
      hasUnsavedChanges={hasUnsavedChanges}
      isForeignExpense={isForeignExpense}
      error={totalAllocationPercent !== 100 ? `Total: ${totalAllocationPercent}%. Adjust projects to apply.` : null}
      projectAllocations={projectAllocations}
      projectAllocationsLoading={filteredRdExpenseProjectAllocationsLoading || allRdExpenseProjectAllocationsLoading}
      updateProjectAllocation={updateProjectAllocation}
      loadingUpdateAllocations={loadingUpdateAllocations}
      submit={submit}
      getBadgeConfig={(allocation) => getRdCategoryBadgeConfig({ ...allocation, taxYear: fiscalYear?.year })}
      selectedProjectName={selectedRdProject?.name}
      searchKeyword={searchKeyword}
      setSearchKeyword={setSearchKeyword}
    />
  );
};
